import React, { Key, useRef } from "react";
import { IListingSummary } from "../../lib/Listing";
import { ListingVitals, EListingVitalsLayout, IListingVitalsProps } from "./ListingVitals";
import { ListingFlag } from "./ListingFlag";
import {
  ListingFavorite,
  EListingFavoriteBackgroundShape,
} from "./ListingFavorite";
import {
  WsrvImage,
  IWsrvImageProps,
} from "../Media";
import { EWsrvTransform } from "../../lib/Media/util";
import { ILinkFunc } from "../../lib/util";
import { Button } from "../Button";

export enum EListingCardLayouts {
  vertical = "vertical",
  composite = "composite",
}

export interface IListingCardProps {
  layout: EListingCardLayouts;
  listing: IListingSummary;
  media?: React.ReactNode;
  linkFunc?: ILinkFunc;
  linkText?: string;
  linkImage?: boolean;
  linkVitals?: boolean;
  className?: string;
  offset?: boolean;
  maxWidthImage?: number;
  imageProps?: Partial<IWsrvImageProps>;
  vitalsLocationIcon?: boolean;
  vitalsPriceTagIcon?: boolean;
  vitalsIncludeVitalsIcons?: boolean;
  vitalsLayout?: EListingVitalsLayout;
  vitalsClassName?: string;
  vitalsProps?: Partial<IListingVitalsProps>
  displayFlags?: boolean;
  flagPosition?: [boolean, boolean];
  flagIcon?: boolean;
  favoriteSelected?: boolean | null;
  favoritePosition?: [boolean, boolean];
  favoriteBackgroundShape?: EListingFavoriteBackgroundShape;
  keyIndex?: Key | null | undefined;
  vitalsTwoLineAddress?: boolean;
  imageClassProps?: string;
  style?: React.CSSProperties;
  showMlsNumber?: boolean;
  mlsNumberClassNames?: string;
  customAddressFormat?: (listing: IListingSummary) => any;
  showDescriptionOnHover?: boolean;
}

export const ListingCard: React.FC<IListingCardProps> = ({
  layout = EListingCardLayouts.vertical,
  vitalsLayout = EListingVitalsLayout.column1Border0,
  vitalsLocationIcon = true,
  vitalsTwoLineAddress = true,
  vitalsIncludeVitalsIcons,
  vitalsClassName,
  displayFlags = true,
  flagPosition,
  flagIcon,
  favoriteSelected = null,
  favoritePosition,
  favoriteBackgroundShape,
  listing,
  linkFunc,
  linkText,
  media,
  className,
  imageProps,
  offset,
  maxWidthImage = 400,
  keyIndex,
  linkImage = false,
  linkVitals = true,
  imageClassProps = "max-w-full w-full h-auto",
  vitalsProps,
  style,
  showMlsNumber = false,
  mlsNumberClassNames = "",
  customAddressFormat,
  vitalsPriceTagIcon = false,
  showDescriptionOnHover = false,
}) => {
  let image: React.ReactNode =
    media ||
    (listing.defaultImage && (
      <WsrvImage
        urlParams={{
          originURL: listing.defaultImage.url,
          preset: EWsrvTransform.cover3x2,
          width: maxWidthImage,
        }}
        widthMax={maxWidthImage}
        imgProps={{
          className: imageClassProps,
          width: listing.defaultImage.dimensions?.[0],
          height: listing.defaultImage.dimensions?.[1],
          placeholder: <span style={undefined} />,
          wrapperProps: {
            style: {},
          },
          alt: listing.defaultImage.caption || listing.address.text,
        }}
        {...imageProps}
      />
    ));
  linkImage &&
    linkFunc &&
    (image = linkFunc({ href: listing.path, children: image }));
  let vitals: React.ReactNode = (
    <ListingVitals
      listing={listing}
      composite={layout == EListingCardLayouts.composite}
      layout={vitalsLayout}
      locationIcon={vitalsLocationIcon}
      includeVitalsIcons={vitalsIncludeVitalsIcons}
      twoLineAddress={vitalsTwoLineAddress}
      omitAddressParts={["postal"]}
      className={`${vitalsClassName} hide-disabled ${(layout == EListingCardLayouts.composite && linkVitals) &&
        "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/80 to-transparent pt-[5%] text-composite-text drop-shadow-md"
        }`}
      customAddressFormat={customAddressFormat}
      priceTagIcon={vitalsPriceTagIcon}
      {...vitalsProps}
    />
  );
  linkVitals && linkFunc && (vitals = linkFunc({ href: listing.path, children: vitals }));
  !linkVitals && linkFunc && layout == EListingCardLayouts.composite && (
    vitals = (<div className={"absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/80 to-transparent pt-[5%] text-composite-text drop-shadow-md flex flex-row flex-wrap md:flex-nowrap justify-center items-center"}>
      {vitals}
      {linkText && (<div className="text-center">
        <Button
          linkProps={{ href: listing.path, className: "button" }}
          icon={false}
          line={true}
          linkFunc={linkFunc}
        >
          {linkText}
        </Button>
      </div>)}
    </div>)
  )

  var detailsOverlay = useRef(null)
  const onMouseOver = (event: any) => {
    if(showDescriptionOnHover && detailsOverlay?.current) {
      (detailsOverlay.current as HTMLDivElement).style.display = "flex"
    }
  }
  const onMouseLeave = (event: any) => {
    if(showDescriptionOnHover && detailsOverlay?.current) {
      (detailsOverlay.current as HTMLDivElement).style.display = "none"
    }
  }

  return (
    <div className={`listing-card relative ${className}`} key={keyIndex} style={style} onMouseEnter={(e) => onMouseOver(e)} onMouseLeave={(e) => onMouseLeave(e)}>
      <div className="relative">
        {image}
        {displayFlags && listing.flags && listing.flags.length > 0 && (
          <ListingFlag
            flags={listing.flags}
            position={flagPosition}
            icon={flagIcon}
            className="absolute bg-accent text-action-text max-w-full overflow-hidden text-ellipsis"
          />
        )}
        {favoriteSelected !== null && (
          <ListingFavorite
            selected={favoriteSelected}
            toggleFunction={() => { }}
            backgroundShape={favoriteBackgroundShape}
            position={favoritePosition}
            className="absolute"
          />
        )}
        {vitals}
        {showMlsNumber && (
          <span className={`${mlsNumberClassNames} text-sm block`}>MLS Number: {listing.mlsID}</span>
        )}
      </div>
      {linkFunc && layout != EListingCardLayouts.composite && linkText && (
        <div className="text-center">
          <Button
            linkProps={{ href: listing.path, className: "button" }}
            icon={false}
            line={true}
            linkFunc={linkFunc}
          >
            {linkText}
          </Button>
        </div>
      )}
      {showDescriptionOnHover && <div ref={detailsOverlay} className="hidden absolute inset-0 bg-black/80 text-composite-text !font-light flex justify-center items-center p-4">
        {linkFunc && linkFunc({
          className: "!flex h-full w-full items-center justify-center hover:!text-composite-text",
          href:listing.path,
          children:(<span dangerouslySetInnerHTML={{__html: listing.descriptionShort ?? ""}}></span>)
        })}
      </div>}
    </div>
  );
};

export default ListingCard;
